import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Col, Container, Row, Input, Label, Button,
  Alert, Spinner, Badge, FormGroup, Progress,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ResearchDisclaimer from "../../Components/Common/ResearchDisclaimer";
import RequestAccessButton from "../Subscription/RequestAccessButton";
import SymbolPicker from "./SymbolPicker";
import SubscribeButton from "../Subscription/SubscribeButton";
import {
  getResearchOptions, buildResearchPrompt, submitResearchResponse, getResearchPrompt,
} from "../../services/userPredictionService";
import { modelNameOf } from "../../helpers/modelName";

/**
 * Run research through your own AI subscription:
 *   1. pick an asset, horizon and prompt style
 *   2. copy the generated prompt into ChatGPT / Claude / Gemini / ...
 *   3. paste the answer back and pick which model produced it
 *
 * A stepper rather than three side-by-side cards. The old layout put every
 * control on screen at once, which on a phone meant scrolling past a form
 * already filled in to reach the textarea actually needed. A finished step
 * collapses to one line — what was chosen, and a way back to it.
 *
 * Step 3 repeats: one prompt is normally answered by several models in a row, so
 * it stays open and shows which ones have already gone in.
 */

/**
 * A finished step: its number, what was chosen, and a way back.
 *
 * `actions` is for controls that have to stay reachable while the step is
 * collapsed. Copy is the obvious one — the whole point of the prompt is to put
 * it on the clipboard, and having to expand the step first to reach the button
 * puts a click in front of the one thing everybody does.
 */
const DoneStep = ({ n, title, summary, onEdit, editLabel = "Change", actions = null }) => (
  <Card className="mb-2">
    <CardBody className="py-2 px-3 d-flex align-items-center justify-content-between gap-2">
      <div className="d-flex align-items-center gap-2 text-truncate">
        <Badge color="success" pill><i className="ri-check-line" /></Badge>
        <span className="text-muted fs-13 flex-shrink-0">{n}.</span>
        <span className="fw-medium text-truncate">{title}</span>
        {summary && (
          <span className="text-muted fs-13 text-truncate d-none d-sm-inline">{summary}</span>
        )}
      </div>
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        {actions}
        {onEdit && (
          <Button size="sm" color="link" className="p-0" onClick={onEdit}>
            {editLabel}
          </Button>
        )}
      </div>
    </CardBody>
  </Card>
);

/** The step being worked on. */
const ActiveStep = ({ n, title, children }) => (
  <Card className="mb-3">
    <CardBody>
      <h5 className="card-title mb-3">
        <Badge color="primary" className="me-2">{n}</Badge>
        {title}
      </h5>
      {children}
    </CardBody>
  </Card>
);

/**
 * What is left of this month's allowance.
 *
 * The page said "13 of 15 prompts left this month" in grey text below the
 * form, which is where a reader looks last and states the one number that
 * matters least: what is left, with no sense of how quickly it goes. A bar
 * shows the shape of it, and it sits above the wizard because it is context
 * for the decision, not a footnote to it.
 *
 * Three states, and they are genuinely different rather than variations:
 * unlimited (an admin, or a plan with no cap) has no bar to draw; a plan with
 * room shows how much; a plan that is spent has to say when it comes back and
 * offer the way out.
 */
const UsagePanel = ({ access }) => {
  const usage = access?.prompt_usage;
  if (!usage) return null;

  const tierName = access.tier_name || (access.admin ? "Admin" : "Free");
  const next = access.next_tier;

  if (usage.unlimited) {
    return (
      <Card className="mb-3">
        <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
          <div>
            <span className="fw-medium">{tierName}</span>
            <span className="text-muted ms-2">Unlimited research prompts.</span>
          </div>
          <Badge color="success">No monthly cap</Badge>
        </CardBody>
      </Card>
    );
  }

  const used = Number(usage.used) || 0;
  const limit = Number(usage.limit) || 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const spent = usage.remaining === 0;
  // Amber before the wall, not at it: finding out you are out of prompts at the
  // moment you wanted one is the version of this that wastes someone's time.
  const colour = spent ? "danger" : pct >= 80 ? "warning" : "success";
  const resets = usage.resetsAt
    ? new Date(usage.resetsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  return (
    <Card className="mb-3">
      <CardBody className="py-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div>
            <span className="fw-medium">{tierName}</span>
            <span className="text-muted ms-2">
              {used} of {limit} research prompts used this month
            </span>
            {usage.unknown && (
              <span className="text-muted fs-12 ms-2">(count unavailable)</span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className={`fw-medium text-${colour}`}>
              {usage.remaining} left
            </span>
            {next && (
              <SubscribeButton
                size="sm"
                label={`Upgrade to ${next.name} — $${next.price_usd}/mo`}
              />
            )}
          </div>
        </div>

        <Progress value={pct} color={colour} style={{ height: 6 }} />

        <div className="text-muted fs-12 mt-2">
          {spent
            ? `You have used this month's allowance.${resets ? ` It resets on ${resets}.` : ""}`
            : resets
              ? `Resets on ${resets}. Unused prompts do not carry over.`
              : "Unused prompts do not carry over."}
          {next && ` ${next.name} raises it to ${next.description || "a higher allowance"}.`}
        </div>
      </CardBody>
    </Card>
  );
};

const GenerateResearch = () => {
  document.title = "Generate Research | ForecastNest";

  // ?prompt=<id>: an admin reopening a finished question to add a missed
  // model. The wizard starts at step 3, as it was before "Done" was clicked.
  const location = useLocation();
  const resumeId = new URLSearchParams(location.search).get("prompt");
  const [resumeFrom, setResumeFrom] = useState(null); // prediction id to go back to

  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null); // { type, text }

  // Step 1 — request
  const [symbol, setSymbol] = useState("");
  const [timeframe, setTimeframe] = useState("1d");
  // The baseline, typed rather than fetched. See the note beside the field.
  // No price field. Establishing what the asset is trading at is part of the
  // task now, and the model is graded on it -- see the settle dialog, where the
  // real price is entered once, afterwards, as a fact rather than a lookup.
  const [style, setStyle] = useState("");
  const [editingRequest, setEditingRequest] = useState(false);

  // Step 2 — generated prompt
  const [built, setBuilt] = useState(null);
  const [building, setBuilding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  // Step 3 — responses. One prompt, several models.
  // No modelId here: which model an answer came from is read from its own
  // self-report on save, not chosen from a list beforehand -- see
  // savePrediction on the server.
  const [responseText, setResponseText] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [answered, setAnswered] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  // Set when the server could not file an answer because the model it claims to
  // be has already answered this prompt. That collision is how a misreported
  // model shows up, and DeepSeek is the one that misreports -- so the member is
  // asked, rather than the row being refused and fixed by hand afterwards.
  // Holds the server's { claimed, collidesWith, suggest } and the text it
  // belongs to, because the textarea is cleared on a successful save.
  const [identityQuery, setIdentityQuery] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getResearchOptions();
        setOptions(res.data);
        // The server names the starting symbol; the head of the list is only
        // the fallback. It used to be read positionally, which meant the
        // wizard opened on whichever group the config happened to list first.
        const opening = res.data?.defaultAsset || res.data?.assets?.[0]?.symbol;
        if (opening) setSymbol(opening);

        const runnableHorizons = (res.data?.timeframes || []).filter((t) => t.allowed !== false);
        if (runnableHorizons.length && !runnableHorizons.some((t) => t.id === "1d")) {
          setTimeframe(runnableHorizons[0].id);
        }

        const allStyles = res.data?.promptStyles || [];
        const mayBuyData = res.data?.access ? res.data.access.canCreate : true;
        const runnable = allStyles.filter((s) => mayBuyData || s.freeTier);
        const preferred = runnable.find((s) => s.isDefault) || runnable[0];
        if (preferred) setStyle(preferred.id);

        if (resumeId) {
          const resumed = await getResearchPrompt(resumeId);
          const p = resumed.data;
          setSymbol(p.symbol);
          setTimeframe(p.timeframe);
          setStyle(p.style);
          setBuilt(p);
          setAnswered(p.answeredModels || []);
          setIsPublic(!!p.isPublic);
          setResumeFrom(new URLSearchParams(location.search).get("from"));
        }
      } catch (e) {
        setFeedback({
          type: "danger",
          text: resumeId
            ? errorText(e, "Could not reopen that prompt.")
            : "Could not load the research options.",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [resumeId]);

  const errorText = (e, fallback) =>
    (typeof e === "string" && e) || e?.error || e?.message || fallback;

  const onBuild = async () => {
    setBuilding(true);
    setFeedback(null);
    setLastSaved(null);
    setCopied(false);
    try {
      const res = await buildResearchPrompt({ symbol, timeframe, style });
      setBuilt(res.data);
      setAnswered(res.data.answeredModels || []);
      setResponseText("");
      setEditingRequest(false);
      setShowPrompt(false);
      // One prompt just left the allowance; decrement locally rather than
      // refetching options, so the number the member sees stays true.
      setOptions((prev) => {
        const usage = prev?.access?.prompt_usage;
        if (!usage || usage.unlimited) return prev;
        return {
          ...prev,
          access: {
            ...prev.access,
            prompt_usage: {
              ...usage,
              used: usage.used + 1,
              remaining: Math.max(0, usage.remaining - 1),
            },
          },
        };
      });
    } catch (e) {
      setBuilt(null);
      setFeedback({ type: "danger", text: errorText(e, "Could not build the prompt.") });
    } finally {
      setBuilding(false);
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(built.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setFeedback({
        type: "warning",
        text: "Clipboard blocked by the browser — select the prompt text and copy it manually.",
      });
    }
  };

  // `confirmModel` is only set by the identity dialog below. The text is passed
  // explicitly because a confirmation is answered after the textarea may have
  // been cleared, so the dialog keeps its own copy.
  const save = async ({ text, confirmModel = null }) => {
    setSaving(true);
    setFeedback(null);
    try {
      const res = await submitResearchResponse({
        promptId: built.promptId,
        responseText: text,
        isPublic,
        confirmModel,
      });
      if (res.data?.needsConfirmation) {
        setIdentityQuery({ ...res.data.needsConfirmation, text });
        return;
      }
      setIdentityQuery(null);
      setLastSaved(res.data);
      setAnswered(res.data.answeredModels || []);
      setResponseText("");
    } catch (e) {
      setIdentityQuery(null);
      setFeedback({ type: "danger", text: errorText(e, "Could not save that response.") });
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = () => save({ text: responseText });

  const styles = options?.promptStyles || [];
  const selectedStyle = styles.find((s) => s.id === style);
  const horizons = options?.timeframes || [];
  const horizonLabel = (id) => (horizons.find((t) => t.id === id) || {}).label || id;

  const canBuyData = options?.access ? options.access.canCreate : true;
  const quota = options?.access?.prompt_usage || null;
  const styleAllowed = (s) => canBuyData || !!s.freeTier;
  const canCreate = canBuyData || styles.some((s) => s.freeTier);
  const outOfQuota = !!(quota && !quota.unlimited && quota.remaining === 0);

  // Step 1 is settled once a prompt exists and the member is not reopening it.
  const requestDone = !!built && !editingRequest;

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Spinner /> <span className="ms-2">Loading research options…</span>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Generate Research" pageTitle="My Research" />
        <ResearchDisclaimer className="mb-3" />

        <UsagePanel access={options?.access} />

        {feedback && (
          <Alert color={feedback.type} toggle={() => setFeedback(null)}>
            {feedback.text}
          </Alert>
        )}

        {/*
            Effectively unreachable now: canCreate is true whenever any style is
            available to a free membership, and self-research is. Kept as a real
            fallback rather than deleted — the styles come from config, and one
            edit there could close this door again.
        */}
        {!canCreate && (
          <Row className="justify-content-center">
            <Col md={9} lg={7}>
              <Card className="mt-2">
                <CardBody className="text-center p-4">
                  <i className="ri-lock-2-line display-5 text-muted" />
                  <h5 className="mt-3">Research is not available on this account</h5>
                  <p className="text-muted">
                    Every membership can generate research, including the free one, so this is
                    unusual — most likely the monthly allowance is spent. It resets on the
                    first of the month.
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <RequestAccessButton />
                    <Link to="/my-research" className="btn btn-light">
                      Browse shared research
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {canCreate && (
          <Row className="justify-content-center">
            {/* One column throughout. Wide screens get a readable measure instead
                of three columns of form; phones get no horizontal crowding. */}
            <Col xxl={8} xl={9} lg={10}>

              {/* ── Step 1 — what to research ───────────────────────────── */}
              {requestDone ? (
                <DoneStep
                  n={1}
                  title={`${built.symbol} · ${horizonLabel(built.timeframe)}`}
                  summary={selectedStyle?.label || ""}
                  onEdit={() => setEditingRequest(true)}
                />
              ) : (
                <ActiveStep n={1} title="Choose what to research">
                  <SymbolPicker
                    value={symbol}
                    suggested={options?.assets || []}
                    onChange={(next) => setSymbol(next)}
                  />

                  <Row>
                    <Col sm={6}>
                      <FormGroup>
                        <Label for="timeframe">Forecast horizon</Label>
                        <Input type="select" id="timeframe" value={timeframe}
                               onChange={(e) => setTimeframe(e.target.value)}>
                          {horizons.map((t) => (
                            <option key={t.id} value={t.id} disabled={t.allowed === false}>
                              {t.label}{t.allowed === false ? " — subscribers" : ""}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col sm={6}>
                      <FormGroup>
                        <Label for="style">Prompt style</Label>
                        <Input type="select" id="style" value={style}
                               onChange={(e) => setStyle(e.target.value)}>
                          {styles.map((s) => (
                            <option key={s.id} value={s.id} disabled={!styleAllowed(s)}>
                              {s.label}{!styleAllowed(s) ? " — subscribers" : ""}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {selectedStyle && (
                    <p className="text-muted fs-12 mb-2">{selectedStyle.description}</p>
                  )}

                  {horizons.some((t) => t.allowed === false) && (
                    <p className="text-muted fs-12 mb-2">
                      Longer horizons need a subscription — a forecast is only worth making
                      if you are still here when it resolves.
                    </p>
                  )}

                  {!canBuyData && (
                    <Alert color="light" className="fs-12 border py-2">
                      Your AI does the looking — nothing here sends market data, and
                      you record the closing price yourself when the window shuts. A
                      membership raises the monthly allowance; the research works the
                      same either way.
                      <div className="mt-2"><RequestAccessButton /></div>
                    </Alert>
                  )}

                  <Button color="primary" className="w-100" onClick={onBuild}
                          disabled={building || !symbol || outOfQuota}>
                    {building ? <><Spinner size="sm" /> Building prompt…</> : "Generate prompt"}
                  </Button>
                </ActiveStep>
              )}

              {/* ── Step 2 — the prompt ─────────────────────────────────── */}
              {built && !editingRequest && (
                showPrompt ? (
                  <ActiveStep n={2} title="Copy this into your AI">
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <Button color={copied ? "success" : "primary"} onClick={onCopy}>
                        <i className="ri-file-copy-line align-bottom me-1" />
                        {copied ? "Copied!" : "Copy prompt"}
                      </Button>
                      <Button color="light" onClick={onBuild} disabled={building}>
                        <i className="ri-refresh-line align-bottom me-1" />
                        Restart the window
                      </Button>
                      <Button color="light" onClick={() => setShowPrompt(false)}>Hide</Button>
                    </div>
                    <Input type="textarea" rows={12} readOnly value={built.prompt}
                           className="font-monospace fs-12" />
                    <p className="text-muted fs-12 mt-2 mb-0">
                      The forecast window starts {new Date(built.priceAsOf).toLocaleString()},
                      so paste the answer back within the hour — after that the prompt
                      expires, because an answer arriving later would be forecasting a period
                      that has partly happened. Finding the current price is the model's job:
                      what it reports is recorded and checked against the real price when you
                      settle.
                    </p>
                  </ActiveStep>
                ) : (
                  <DoneStep
                    n={2}
                    title="Prompt ready"
                    summary={`${built.prompt.length.toLocaleString()} chars · window from ${new Date(built.priceAsOf).toLocaleTimeString()}`}
                    onEdit={() => setShowPrompt(true)}
                    editLabel="Show"
                    actions={
                      <Button size="sm" color={copied ? "success" : "primary"} onClick={onCopy}>
                        <i className="ri-file-copy-line align-bottom me-1" />
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                    }
                  />
                )
              )}

              {/* ── Step 3 — the answers, one model at a time ───────────── */}
              {built && !editingRequest && (
                <ActiveStep n={3} title="Paste the answer back">
                  {/* Read from the saved rows rather than held in the page, so a
                      reload mid-session still knows what has gone in. */}
                  {answered.length > 0 && (
                    <div className="mb-3">
                      <div className="text-muted fs-12 mb-1">Already recorded:</div>
                      <div className="d-flex flex-wrap gap-1">
                        {answered.map((m) => (
                          <Badge key={m.modelId} color="success" className="fw-normal">
                            <i className="ri-check-line align-bottom me-1" />{m.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No "which model" picker: the answer names itself (see
                      matchModelFromSelfReport on the server), and a paste that
                      duplicates a model already recorded against this prompt,
                      or duplicates a previous paste's text outright, is
                      rejected on save with a reason rather than caught here
                      ahead of time. */}
                  <FormGroup check className="mb-3">
                    <Input className="form-check-input" type="checkbox" id="isPublic"
                           checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                    <Label className="form-check-label fs-13" for="isPublic">
                      Share publicly
                    </Label>
                  </FormGroup>

                  {/* The identity question on save only works if DeepSeek comes
                      after every other maker: then whichever model it claims
                      to be has already answered, and the collision exposes it. */}
                  <p className="text-muted fs-12 mb-2">
                    <i className="ri-information-line align-bottom me-1" />
                    Paste DeepSeek's answer last. It sometimes gives another model's name,
                    and arriving after every other maker is how it gets caught.
                  </p>
                  <FormGroup>
                    <Input type="textarea" id="response" rows={7} value={responseText}
                           placeholder="Paste the complete answer here, including the JSON block."
                           onChange={(e) => setResponseText(e.target.value)} />
                  </FormGroup>

                  <div className="d-flex flex-wrap gap-2">
                    <Button color="success" onClick={onSubmit}
                            disabled={saving || !responseText.trim()}>
                      {saving ? <><Spinner size="sm" /> Saving…</> : "Save as my research"}
                    </Button>
                    {resumeFrom ? (
                      <Link to={`/prediction/${resumeFrom}`} className="btn btn-light">
                        Done — back to the forecast
                      </Link>
                    ) : (
                      <Link to="/my-research" className="btn btn-light">
                        Done — view my research
                      </Link>
                    )}
                  </div>

                  {lastSaved && (
                    <Alert color="success" className="mt-3 mb-0 fs-13">
                      Saved {lastSaved.symbol} via {modelNameOf(lastSaved)} — range{" "}
                      <strong>${lastSaved.range_min} – ${lastSaved.range_max}</strong> at{" "}
                      {lastSaved.confidence}% confidence.{" "}
                      {lastSaved.is_private ? "Only you can see it." : "It is shared publicly."}
                      <div className="mt-1 text-muted">
                        Paste another model's answer above to add it to the same prompt.
                      </div>
                    </Alert>
                  )}
                </ActiveStep>
              )}

            </Col>
          </Row>
        )}

        {/*
          One question, asked at the only moment it can be answered reliably:
          the member has the answer in front of them and knows which model they
          just pasted. Declining changes nothing -- the row was never written.
        */}
        <Modal isOpen={!!identityQuery} toggle={() => setIdentityQuery(null)} centered>
          <ModalHeader toggle={() => setIdentityQuery(null)}>
            Which model wrote this answer?
          </ModalHeader>
          <ModalBody className="fs-14">
            <p className="mb-2">
              This answer says it is{" "}
              <strong>{identityQuery?.claimed || "a model it did not name"}</strong>, and{" "}
              {identityQuery?.canSaveAsIs ? (
                <>
                  <strong>{identityQuery?.collidesWith}</strong> — another{" "}
                  {identityQuery?.maker} model — has already answered this prompt.
                </>
              ) : (
                <>
                  <strong>{identityQuery?.collidesWith}</strong> has already answered this
                  prompt.
                </>
              )}
            </p>
            <p className="mb-2">
              Models sometimes get their own name wrong. If you pasted DeepSeek, it can be
              filed as <strong>{identityQuery?.suggest?.version}</strong> — the version your
              last DeepSeek answer used.
            </p>
            {/*
              One maker can legitimately appear twice: running Claude Opus and
              then Claude Sonnet on one prompt is two models, not a misreport.
              An exact repeat of the same model has no such reading, so that
              case keeps the delete-the-earlier-row instruction instead.
            */}
            <p className="text-muted mb-0">
              {identityQuery?.canSaveAsIs
                ? `Nothing has been saved yet. If you did run two ${identityQuery?.maker} models on this prompt, save it as ${identityQuery?.filedAs}.`
                : `Nothing has been saved yet. If it really is ${identityQuery?.collidesWith}, close this and delete the earlier answer first.`}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIdentityQuery(null)} disabled={saving}>
              Cancel
            </Button>
            {identityQuery?.canSaveAsIs ? (
              <Button
                color="light"
                disabled={saving}
                onClick={() => save({ text: identityQuery.text, confirmModel: "as-is" })}
              >
                No, save as {identityQuery?.filedAs}
              </Button>
            ) : null}
            <Button
              color="primary"
              disabled={saving}
              onClick={() => save({ text: identityQuery.text, confirmModel: "deepseek" })}
            >
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              Yes, save as {identityQuery?.suggest?.version}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default GenerateResearch;
